/**
 * @render react
 * @name IFrame
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import renderAmp from './amp';

import './_style.scss';

/**
 * IFrame component
 *
 * @param {string} iframeUrl - URL for the iframe
 * @param {string} iframeBorder - border of the iframe. Default set to 0
 * @param {string} iframeHeight - height of the iframe. This will be overridden is loadStyles is set to true for responsiveness
 * @param {string} iframeStyle - inline style of the iframe
 * @param {string} iframeWidth - width of the iframe. This will be overridden is loadStyles is set to true for responsiveness
 * @param {string} scrolling - Whether iframe is scrollable. Values can be yes/no/auto
 * @param {string} loading - Prop for lazyLoading
 * @param {boolean} loadStyles - Whether to apply the class names for styling.
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 */
class IFrame extends PureComponent {
  amp() {
    return renderAmp.bind(this)();
  }

  render() {
    const {
      context: { cssPrefix },
      extraClassNames,
      iframeBorder,
      iframeHeight,
      iframeName,
      iframeStyle,
      iframeUrl,
      iframeWidth,
      loading,
      loadStyles,
      onlyFrame,
      scrolling,
    } = this.props;

    if (!iframeUrl) return null;

    const iframeProps = {
      src: iframeUrl,
      scrolling,
      name: iframeName,
      height: iframeHeight,
      width: iframeWidth,
      loading,
      style: iframeStyle,
    };

    if (onlyFrame) {
      return (
        <iframe
          {...iframeProps}
          className={classNames({
            [`${cssPrefix}__iframe--frame`]: loadStyles,
            ...extraClassNames,
          })}
          frameBorder={iframeBorder}
          title={`iframe to ${iframeUrl}`}
        />
      );
    }

    return (
      <div
        className={classNames({
          [`${cssPrefix}__iframe`]: loadStyles,
          ...extraClassNames,
        })}
      >
        <iframe
          {...iframeProps}
          className={`${cssPrefix}__iframe--frame`}
          title={`iframe to ${iframeUrl}`}
        />
      </div>
    );
  }
}

IFrame.propTypes = {
  ampLayout: PropTypes.string,
  ampScripts: PropTypes.shape(),
  context: PropTypes.shape(),
  extraClassNames: PropTypes.shape(),
  iframeBorder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iframeHeight: PropTypes.string,
  iframeName: PropTypes.string,
  iframeStyle: PropTypes.shape(),
  iframeUrl: PropTypes.string,
  iframeWidth: PropTypes.string,
  loading: PropTypes.string,
  loadStyles: PropTypes.bool,
  onlyFrame: PropTypes.bool,
  scrolling: PropTypes.string,
};

IFrame.defaultProps = {
  ampLayout: 'responsive',
  ampScripts: {
    'amp-iframe': 'https://cdn.ampproject.org/v0/amp-iframe-0.1.js',
  },
  context: {},
  extraClassNames: {},
  iframeBorder: 0,
  iframeHeight: '100%',
  iframeName: '',
  iframeStyle: {},
  iframeUrl: '',
  iframeWidth: '100%',
  loading: 'lazy',
  loadStyles: true,
  onlyFrame: false,
  scrolling: 'no',
};

export default compose(withPolaris, withAmp)(IFrame);
